import { createContext, useState, useContext, useEffect } from 'react';
import useGetCart from '../Hooks/useGetCart';
import { useAuth } from './AuthProvider';
import {
    DeleteAllProducts, DeleteProduct, GetBasket, LoadBasket, LoadBasketFromOrders, AddProductsToBasket,
    AddProductToBasket, UpdateProductInBasket
} from '../Services/BasketService';
import { toast } from "react-toastify"
import { useConfig } from './ConfigProvider';
import { useParams } from './ParamsProvider';

const CartContext = createContext();

export const useCart = () => {
    return useContext(CartContext);
};

export const CartProvider = ({ children }) => {

    const { authState } = useAuth()
    const { cartItems, setCartItems, handleClearCartItems, handleDeleteAllItems } = useGetCart()
    const { api } = useConfig()
    const { basketItemsCountLimit } = useParams()

    const [basketTotalPriceNet, setBasketTotalPriceNet] = useState(0)
    const [basketTotalPriceGross, setBasketTotalPriceGross] = useState(0)
    const [basketTotalTax, setBasketTotalTax] = useState(0)

    const [cartTotalNet, setCartTotalNet] = useState(0)
    const [cartTotalGross, setCartTotalGross] = useState(0)
    const [cartTotalTax, setCartTotalTax] = useState(0)

    useEffect(() => {
        if (cartItems && cartItems.basketItems && cartItems.basketItems.length > 0) {
            var result = getBasketTotalValue(cartItems.basketItems)
            setCartTotalNet(result.totalNet)
            setCartTotalGross(result.totalGross)
            setCartTotalTax(result.totalTax)
        }
        else {
            setCartTotalNet(0)
            setCartTotalGross(0)
            setCartTotalTax(0)
        }
    }, [cartItems])

    useEffect(() => {
        if (cartItems && cartItems.basketItems) {
            setBasketTotalPriceNet(calculateTotalCostNet(cartItems.basketItems))
            setBasketTotalPriceGross(calculateTotalCostGross(cartItems.basketItems))
            calculateTaxValue(cartItems.basketItems)
        }
        else {
            setBasketTotalPriceNet(0)
            setBasketTotalPriceGross(0)
            calculateTaxValue(0)
        }
    }, [cartItems])

    useEffect(() => {
        calculateTaxValue()
    }, [basketTotalPriceNet])

    const round2 = (num) => Math.round((num + Number.EPSILON) * 100) / 100; //DK : Zaokrąglania do 2 miejsc po przecinku

    const getGrossValue = (costNet, qty, taxRate) => {
        let res = round2(costNet * qty * (1 + taxRate / 100))
        return res
    }

    const getBasketTotalValue = (basketItems, { roundPerLine = false } = {}) => {
        const normalizeTaxRate = (raw) => {
            if (raw == null) return 0;
            if (typeof raw === 'string') {
                if (raw.includes('%')) raw = raw.replace('%', '');
                raw = raw.trim();
            }
            const num = Number(raw);
            if (Number.isNaN(num)) return 0;
            return num > 1 ? num / 100 : num;
        };

        let totalNet = 0;
        let totalTax = 0;
        let totalGross = 0;
        const breakdown = {};

        basketItems.filter(bi => bi.isSelected).forEach(item => {
            const qty = Number(item.qty) || 0;
            const costNet = Number(item.costNet) || 0;
            // const rawTax = item.product?.taxRate ?? item.taxRate ?? 0;
            const rawTax = item.product?.taxRate ?? 0;
            const taxRate = normalizeTaxRate(rawTax);

            let lineNet = costNet * qty;
            let lineTax = lineNet * taxRate;
            let lineGross = lineNet + lineTax;

            if (roundPerLine) {
                // jeśli chcesz zaokrąglać każdą pozycję (np. do centów) przed sumowaniem:
                const rNet = round2(lineNet);
                const rTax = round2(lineTax);
                lineNet = rNet;
                lineTax = rTax;
                lineGross = round2(lineNet + lineTax);
            }

            totalNet += lineNet;
            totalTax += lineTax;
            totalGross += lineGross;

            const rateKey = `${Math.round(taxRate * 100)}%`;
            if (!breakdown[rateKey]) breakdown[rateKey] = { net: 0, tax: 0, gross: 0 };

            breakdown[rateKey].net += lineNet;
            breakdown[rateKey].tax += lineTax;
            breakdown[rateKey].gross += lineGross;
        });

        // zaokrąglamy wyniki końcowe
        Object.keys(breakdown).forEach(k => {
            breakdown[k].net = round2(breakdown[k].net);
            breakdown[k].tax = round2(breakdown[k].tax);
            breakdown[k].gross = round2(breakdown[k].gross);
        });

        return {
            totalNet: round2(totalNet),
            totalTax: round2(totalTax),
            totalGross: round2(totalGross),
            breakdown
        };
    };

    const getBasketTotalValueOffer = (basketItems, { roundPerLine = false } = {}) => {
        const normalizeTaxRate = (raw) => {
            if (raw == null) return 0;
            if (typeof raw === 'string') {
                if (raw.includes('%')) raw = raw.replace('%', '');
                raw = raw.trim();
            }
            const num = Number(raw);
            if (Number.isNaN(num)) return 0;
            return num > 1 ? num / 100 : num;
        };

        let totalNet = 0;
        let totalTax = 0;
        let totalGross = 0;
        const breakdown = {};

        basketItems.forEach(item => {
            const qty = Number(item.qty) || 0;
            const costNet = Number(item.costNet) || 0;
            // const rawTax = item.product?.taxRate ?? item.taxRate ?? 0;
            const rawTax = item.product?.taxRate ?? 0;
            const taxRate = normalizeTaxRate(rawTax);

            let lineNet = costNet * qty;
            let lineTax = lineNet * taxRate;
            let lineGross = lineNet + lineTax;

            if (roundPerLine) {
                // jeśli chcesz zaokrąglać każdą pozycję (np. do centów) przed sumowaniem:
                const rNet = round2(lineNet);
                const rTax = round2(lineTax);
                lineNet = rNet;
                lineTax = rTax;
                lineGross = round2(lineNet + lineTax);
            }

            totalNet += lineNet;
            totalTax += lineTax;
            totalGross += lineGross;

            const rateKey = `${Math.round(taxRate * 100)}%`;
            if (!breakdown[rateKey]) breakdown[rateKey] = { net: 0, tax: 0, gross: 0 };

            breakdown[rateKey].net += lineNet;
            breakdown[rateKey].tax += lineTax;
            breakdown[rateKey].gross += lineGross;
        });

        // zaokrąglamy wyniki końcowe
        Object.keys(breakdown).forEach(k => {
            breakdown[k].net = round2(breakdown[k].net);
            breakdown[k].tax = round2(breakdown[k].tax);
            breakdown[k].gross = round2(breakdown[k].gross);
        });

        return {
            totalNet: round2(totalNet),
            totalTax: round2(totalTax),
            totalGross: round2(totalGross),
            breakdown
        };
    };

    const sortBasketItems = (basketItems) => {
        if (!Array.isArray(basketItems)) {
            return []
        }

        return [...basketItems].sort((a, b) => {
            const dateA = new Date(a.addToBasketDate)
            const dateB = new Date(b.addToBasketDate)

            // najpierw sortowanie po dacie rosnąco (najnowsze na dół)
            const dateDiff = dateA - dateB
            if (dateDiff !== 0) {
                return dateDiff
            }

            // jeżeli data jest taka sama -> sortowanie po ArtNo malejąco (DESC)
            if (a.ArtNo > b.ArtNo) return -1
            if (a.ArtNo < b.ArtNo) return 1

            return 0
        })
    }

    const GetProductQuantityInCart = (product) => {
        if (!authState.user || cartItems.basketItems === null || cartItems.basketItems === undefined) {
            return 0
        }
        var result = cartItems.basketItems.find(e => e.product.index === product.index)
        if (result) {
            return result.qty
        }
        return 0
    }

    const addProductToBasket = async (index, qty) => {
        if (!authState.user) {
            toast.warning('Prosze się zalogować')
            return false
        }
        if (!authState.custId) {
            toast.warning('Brak wybranego kontrahenta')
            return false
        }
        if (cartItems && cartItems.basketItems && cartItems.basketItems.length >= basketItemsCountLimit) {
            toast.warning(`Osiągnięto maksymalny limit koszyka który wynosi ${basketItemsCountLimit} pozycji!`)
            return false
        }
        try {
            let res = await AddProductToBasket(api, { index: index, qtyStock: qty }, authState.custId, authState.siteId, authState.whId)
            const newItem = res.data
            setCartItems(prev => {
                const existingItemIndex = prev.basketItems.findIndex(
                    item => item.artNo === newItem.artNo
                )
                let updatedBasketItems
                if (existingItemIndex > -1) {
                    updatedBasketItems = prev.basketItems.map((item, i) => {
                        if (i === existingItemIndex) {
                            return {
                                ...item,
                                qty: item.qty + newItem.qt
                            }
                        }
                        return item
                    })
                } else {
                    updatedBasketItems = [...prev.basketItems, newItem]
                }
                return {
                    ...prev,
                    basketItems: sortBasketItems(updatedBasketItems)
                }
            })
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const updateProductInBasket = async (index, qty) => {
        if (!authState.user) {
            toast.warning('Prosze się zalogować')
            return false
        }
        if (!authState.custId) {
            toast.warning('Brak wybranego kontrahenta')
            return false
        }
        try {
            let res = await UpdateProductInBasket(api, { index: index, qtyStock: qty }, authState.custId, authState.siteId, authState.whId)
            setCartItems(prev => {
                const updatedBasketItems = prev.basketItems.map(item => {
                    if (item.artNo === index) {
                        return res.data
                    }
                    return item
                })
                return {
                    ...prev,
                    basketItems: sortBasketItems(updatedBasketItems),
                }
            })
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const addProductsToBasket = async (products, replaceBasket) => {
        if (!products || products.length === 0) {
            return false
        }
        if (!authState.user) {
            toast.warning('Prosze się zalogować')
            return false
        }
        if (!authState.custId) {
            toast.warning('Brak wybranego kontrahenta')
            return false
        }
        try {
            const res = await AddProductsToBasket(api, products, authState.custId, authState.siteId, authState.whId, replaceBasket)
            if (!res.data) {
                return false
            }
            setCartItems(res.data)
            return true
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const deleteInCart = async (product) => {
        try {
            await DeleteProduct(api, product, authState.custId)
            setCartItems(prev => {
                const updatedBasketItems = prev.basketItems.filter(item => item.artNo !== product.index)
                return {
                    ...prev,
                    basketItems: updatedBasketItems,
                }
            })
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const deleteAllItems = async () => {
        try {
            let res = await DeleteAllProducts(api, authState.custId)
            handleDeleteAllItems()
            return true
        } catch (e) {
            console.log(e)
            return false
        }
    }

    const loadBasket = async (basketIds, replaceBasket) => {
        try {
            const res = await LoadBasket(api, basketIds, authState.custId, replaceBasket, authState.siteId, authState.whId)
            setCartItems(res.data)
            return true
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const loadBasketFromOrders = async (orderIds, replaceBasket) => {
        try {
            const res = await LoadBasketFromOrders(api, orderIds, authState.custId, replaceBasket, authState.siteId, authState.whId)
            setCartItems(res.data)
            return true
        } catch (e) {
            console.log(e)
            return false
        }
    }

    const calculateTotalCostNet = (basketItems) => {
        return basketItems.reduce((total, product) => {
            if (product.isSelected) {
                return total + (product.costNet * product.qty)
            }
            return total
        }, 0)
    }

    const calculateTotalCostGross = (basketItems) => {
        return basketItems.reduce((total, product) => {
            if (product.isSelected) {
                return total + (product.costGross * product.qty)
            }
            return total
        }, 0)
    }

    const calculateTaxValue = () => {
        setBasketTotalTax(basketTotalPriceGross - basketTotalPriceNet)
    }

    const getBasket = (calculatePrices = true) => {
        GetBasket(api, authState.custId, authState.siteId, authState.whId, calculatePrices)
            .then(res => { setCartItems(res.data) })
            .catch(e => {
                console.log(e)
            })
    }

    const getBasketAsync = async (calculatePrices = true) => {
        try {
            let res = await GetBasket(api, authState.custId, authState.siteId, authState.whId, calculatePrices)
            setCartItems(res.data)
            return res.data
        } catch (error) {
            console.log(error)
            return false
        }
    }

    return (
        <CartContext.Provider value={{
            cartItems,
            cartTotalNet,
            cartTotalGross,
            cartTotalTax,
            basketTotalPriceNet,
            basketTotalPriceGross,
            basketTotalTax,
            deleteInCart,
            deleteAllItems,
            loadBasket,
            GetProductQuantityInCart,
            getBasket,
            getBasketAsync,
            loadBasketFromOrders,
            handleClearCartItems,
            addProductsToBasket,
            addProductToBasket,
            updateProductInBasket,
            getBasketTotalValue,
            getBasketTotalValueOffer,
            round2,
            getGrossValue,
            setCartItems,
        }}>
            {children}
        </CartContext.Provider>
    );
};